import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { VotingSnapshot, VoteChoice, ReturnTarget, PlayerSnapshot, PlayerId } from '@juegazo/shared';

@customElement('jgz-vote-modal')
export class JgzVoteModal extends LitElement {
  createRenderRoot() { return this; }

  @property({ attribute: false }) voting: VotingSnapshot | null = null;
  @property({ type: String }) myPlayerId: PlayerId = '';
  @property({ type: Boolean }) isAdmin = false;
  @property({ type: Boolean }) isLocalMode = false;
  @property({ attribute: false }) players: PlayerSnapshot[] = [];
  @property({ type: String }) currentPlayerId: PlayerId = '';

  private localVoter: PlayerId = '';

  willUpdate(_changed: Map<string, unknown>) {
    // In local (hot-seat) mode there is no per-socket identity, so the modal
    // must let the user pick which player is casting. Default the selector to
    // the initiator so a fresh vote starts with a reachable choice.
    if (this.isLocalMode && !this.players.some((p) => p.id === this.localVoter)) {
      this.localVoter = this.voting?.initiatorId ?? this.currentPlayerId ?? '';
    }
  }

  private onCast = (choice: VoteChoice) => {
    // Online mode: the server attributes the vote to the socket owner, so the
    // event only needs the choice. Local mode (hot-seat) has no per-socket
    // identity, so it carries the player picked in the selector.
    if (!this.isLocalMode) {
      if (!this.myPlayerId) return;
      this.dispatchEvent(new CustomEvent('jgz-vote-cast', {
        detail: { choice }, bubbles: true, composed: true,
      }));
      return;
    }
    if (!this.localVoter) return;
    this.dispatchEvent(new CustomEvent('jgz-vote-cast', {
      detail: { choice, playerId: this.localVoter }, bubbles: true, composed: true,
    }));
  };

  private onDecide = (target: ReturnTarget) => {
    this.dispatchEvent(new CustomEvent('jgz-vote-decide', {
      detail: { target }, bubbles: true, composed: true,
    }));
  };

  private onClear = () => {
    this.dispatchEvent(new CustomEvent('jgz-vote-clear', {
      bubbles: true, composed: true,
    }));
  };

  private wasTie(): boolean {
    if (!this.voting || this.voting.phase !== 'decided') return false;
    const votes = this.voting.votes;
    let cVal = 0;
    let cNoVal = 0;
    for (const choice of Object.values(votes)) {
      if (choice === 'valida') cVal++;
      else cNoVal++;
    }
    return cVal === cNoVal && (cVal + cNoVal) > 0;
  }

  render() {
    if (!this.voting) return nothing;
    if (this.voting.phase === 'casting') return this.renderCasting();
    if (this.voting.phase === 'decided') {
      if (this.voting.outcome === 'valida') return this.renderValida();
      if (this.voting.outcome === 'no_valida' && !this.voting.returnDecision) {
        return this.renderPickerOrWaiting();
      }
      if (this.voting.outcome === 'no_valida' && this.voting.returnDecision) {
        return this.renderDecided();
      }
    }
    return nothing;
  }

  private renderCasting() {
    const v = this.voting!;
    const totalPlayers = this.players.length;
    const votedCount = Object.keys(v.votes).length;
    // In local mode the active "voter" is the player picked in the hot-seat
    // selector, not `myPlayerId` (which is just the current player).
    const activeVoterId = this.isLocalMode ? this.localVoter : this.myPlayerId;
    const myVote = v.votes[activeVoterId];
    return html`
      <div class="modal-overlay">
        <div class="vote-card">
          <h2>¿Discutir una respuesta?</h2>
          <p class="muted">Voten todos. Si la mayoría dice No válida, podrán decidir qué hacer después.</p>
          <p class="muted">${votedCount} de ${totalPlayers} ya votó</p>
          ${this.isLocalMode ? this.renderLocalVoterPicker(v) : nothing}
          <div class="vote-buttons">
            <button
              class="btn-pill green ${myVote === 'valida' ? 'selected' : ''}"
              @click=${() => this.onCast('valida')}
            >
              VÁLIDA
            </button>
            <button
              class="btn-pill primary ${myVote === 'no_valida' ? 'selected' : ''}"
              @click=${() => this.onCast('no_valida')}
            >
              NO VÁLIDA
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderLocalVoterPicker(v: VotingSnapshot) {
    return html`
      <div class="local-voter-row">
        <span class="local-voter-label">¿Quién vota?</span>
        <div class="local-voter-pills">
          ${this.players.map((p) => {
            const alreadyVoted = v.votes[p.id] !== undefined;
            const isSelected = p.id === this.localVoter;
            // A player who already voted is locked so the hot-seat device can't
            // accidentally overwrite their choice while passing around.
            return html`
              <button
                class="local-voter-pill ${isSelected ? 'selected' : ''} ${alreadyVoted ? 'locked' : ''}"
                ?disabled=${alreadyVoted}
                @click=${() => { this.localVoter = p.id; this.requestUpdate?.(); }}
              >
                ${p.displayName}
                ${alreadyVoted ? html`<span class="local-voter-check">✓</span>` : nothing}
              </button>
            `;
          })}
        </div>
      </div>
    `;
  }

  /** Encabezado compartido del resultado de la votación (veredicto + consejo). */
  private renderResultHeader(verdict: 'VÁLIDA' | 'INVÁLIDA', emoji: string) {
    const showTie = this.wasTie();
    return html`
      ${showTie ? html`<div class="tie-spinner">⚡</div>` : nothing}
      <div style="font-size: 3rem;">${emoji}</div>
      <h2>Resultado de la votación</h2>
      <p class="muted">(Considerando mayoría absoluta)</p>
      <p class="vote-verdict ${verdict === 'VÁLIDA' ? 'good' : 'bad'}">${verdict}</p>
      <p class="vote-tip">
        La recomendación es definir una "regla de juego" clara para que todos sepan cómo actuar.
        También conviene pensar siempre en un "particular" o en un "solo elemento" (ser específico), evitando
        respuestas "generales" que después causen distintas interpretaciones.
      </p>
    `;
  }

  private renderValida() {
    return html`
      <div class="modal-overlay">
        <div class="vote-card">
          ${this.renderResultHeader('VÁLIDA', '✅')}
          <div class="vote-close-row">
            <button class="btn-pill yellow vote-close" @click=${this.onClear}>CERRAR</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderPickerOrWaiting() {
    const canPick = this.isAdmin || this.isLocalMode;
    if (!canPick) {
      return html`
        <div class="modal-overlay">
          <div class="vote-card">
            ${this.renderResultHeader('INVÁLIDA', '❌')}
            <p class="muted">Esperando decisión del admin…</p>
          </div>
        </div>
      `;
    }
    return this.renderPicker();
  }

  private renderPicker() {
    const candidates = this.players.filter((p) => p.id !== this.currentPlayerId);
    return html`
      <div class="modal-overlay">
        <div class="vote-card">
          ${this.renderResultHeader('INVÁLIDA', '❌')}
          <p>¿A quién le devolvemos la bomba?</p>
          <div class="picker-list">
            ${candidates.map((p) => html`
              <button class="picker-row" @click=${() => this.onDecide(p.id)}>
                <span class="picker-avatar">${p.displayName.charAt(0).toUpperCase()}</span>
                <span class="picker-name">${p.displayName}</span>
              </button>
            `)}
          </div>
          <div class="picker-divider">○</div>
          <div class="vote-close-row">
            <button class="btn-pill yellow vote-close" @click=${() => this.onDecide('no_return')}>
              NO DEVOLVER
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderDecided() {
    const decision = this.voting!.returnDecision!;
    if (decision.target === 'no_return') {
      return html`
        <div class="modal-overlay">
          <div class="vote-card">
            <div style="font-size: 3rem;">🤝</div>
            <h2>No se devuelve</h2>
            <p class="muted">El juego continúa</p>
            <div class="vote-close-row">
              <button class="btn-pill yellow vote-close" @click=${this.onClear}>CERRAR</button>
            </div>
          </div>
        </div>
      `;
    }
    const player = this.players.find((p) => p.id === decision.target);
    return html`
      <div class="modal-overlay">
        <div class="vote-card">
          <div style="font-size: 3rem;">↩️</div>
          <h2>Bomba devuelta a ${player?.displayName ?? decision.target}</h2>
          <div class="vote-close-row">
            <button class="btn-pill yellow vote-close" @click=${this.onClear}>CERRAR</button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'jgz-vote-modal': JgzVoteModal;
  }
}
